<template>
    <div ref="rootRef" class="embed-root">
        <!-- Ввод запроса: hero как на макете -->
        <section v-if="!submitted" class="hero">
            <p class="hero-eyebrow">
                <ChatIcon name="sparkles" :size="14" color="#e02e30" />
                <span>AI-аналитик авторынка</span>
            </p>
            <h1 class="hero-title">Спросите что угодно об автомобильном рынке России</h1>
            <p class="hero-subtitle">
                Отвечаю по данным авторынка, статистике продаж и кредитов — мгновенно
            </p>

            <form class="hero-form" @submit.prevent="submitHero">
                <span class="hero-form-icon" aria-hidden="true">
                    <ChatIcon name="chat" :size="18" color="#e02e30" />
                </span>
                <input
                    ref="heroInputRef"
                    v-model="heroText"
                    class="hero-input"
                    type="text"
                    :placeholder="placeholder"
                    aria-label="Ваш вопрос"
                />
                <button
                    type="submit"
                    class="hero-send"
                    :disabled="!canSubmitHero"
                    aria-label="Спросить"
                >
                    <ChatIcon name="arrow-up" :size="20" color="#FFFFFF" />
                </button>
            </form>

            <div class="hero-chips">
                <button
                    v-for="suggestion in suggestions"
                    :key="suggestion.id"
                    type="button"
                    class="hero-chip"
                    @click="askChip(suggestion)"
                >
                    {{ suggestion.prompt }}
                </button>
            </div>

            <ul class="hero-trust" aria-label="Преимущества">
                <li v-for="trust in trustMarks" :key="trust.label" class="hero-trust-item">
                    <ChatIcon :name="trust.icon" :size="16" color="#e02e30" />
                    <span>{{ trust.label }}</span>
                </li>
            </ul>
        </section>

        <!-- Запрос передан: уведомление + ручной переход (если вкладка не открылась) -->
        <section v-else class="sent">
            <div class="sent-icon" aria-hidden="true">
                <ChatIcon name="check" :size="30" color="#1a8f3c" />
            </div>
            <h1 class="sent-title">Запрос передан в AI-Аналитик</h1>
            <p class="sent-subtitle">
                Мы открыли ваш запрос в новой вкладке. Если она не открылась автоматически —
                нажмите кнопку ниже.
            </p>
            <p v-if="lastQuery" class="sent-query">«{{ lastQuery }}»</p>

            <div class="sent-actions">
                <a class="sent-open" :href="lastUrl" target="_blank" rel="noopener">
                    Открыть AI-Аналитик
                </a>
                <button type="button" class="sent-again" @click="resetForm">
                    Задать другой вопрос
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { useChatSuggestions } from '@/composables/useChatSuggestions'

const { suggestions } = useChatSuggestions()

const trustMarks = [
    { icon: 'database', label: 'Данные с 2012 года' },
    { icon: 'refresh', label: 'Обновление ежедневно' },
    { icon: 'shield', label: 'Проверенные источники' },
]

const rootRef = ref(null)
const heroInputRef = ref(null)
const heroText = ref('')

const submitted = ref(false)
const lastQuery = ref('')
const lastUrl = ref('')

const placeholder
    = 'Например: динамика продаж Haval за апрель, прогноз цен на 2026, сравнить Chery и Voyah…'

const canSubmitHero = computed(() => Boolean(heroText.value.trim()))

// Полная версия AI-Аналитика на том же origin (am-front). Передаём запрос
// через ?prompt= — ChatPage сам стартует новый диалог и отправляет его.
function buildUrl(text) {
    const base = typeof window !== 'undefined' ? window.location.origin : ''
    return `${base}/chat?prompt=${encodeURIComponent(text)}`
}

function handoff(text) {
    const value = (text || '').trim()
    if (!value) return
    const url = buildUrl(value)
    lastQuery.value = value
    lastUrl.value = url
    submitted.value = true
    // Открываем в новой вкладке по пользовательскому жесту (клик/submit) —
    // обычно проходит мимо popup-блокировщика. Фолбэк-кнопка остаётся на случай,
    // если вкладка всё же не открылась.
    if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener')
    }
    nextTick(postHeight)
}

function submitHero() {
    if (!canSubmitHero.value) return
    handoff(heroText.value)
    heroText.value = ''
}

function askChip(suggestion) {
    handoff(suggestion.prompt)
}

function resetForm() {
    submitted.value = false
    lastQuery.value = ''
    lastUrl.value = ''
    heroText.value = ''
    nextTick(() => {
        heroInputRef.value?.focus()
        postHeight()
    })
}

// ── Авто-высота iframe ───────────────────────────────────────────────
// Сообщаем родительскому окну (WordPress) актуальную высоту контента,
// чтобы плагин подгонял высоту iframe без внутреннего скролла.
let resizeObserver = null

function postHeight() {
    if (typeof window === 'undefined' || window.parent === window) return
    const el = rootRef.value
    if (!el) return
    const height = Math.ceil(el.scrollHeight)
    window.parent.postMessage({ type: 'am-embed-height', height }, '*')
}

onMounted(async () => {
    await nextTick()
    postHeight()
    if (typeof ResizeObserver !== 'undefined' && rootRef.value) {
        resizeObserver = new ResizeObserver(() => postHeight())
        resizeObserver.observe(rootRef.value)
    }
    window.addEventListener('resize', postHeight)
})

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }
    window.removeEventListener('resize', postHeight)
})
</script>

<style scoped>
.embed-root {
    width: 100%;
    background: #ffffff;
    font-family: inherit;
    color: #23282c;
}

/* ── Hero (ввод запроса) ───────────────────────────────── */
.hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 28px 20px 28px;
    /* Жёстко фиксированная высота — синхронно с siteConfig.aiEmbed.initialHeight
       в am-site. Так контейнер iframe и контент embed всегда строго одной
       высоты, без зазоров и скролла. */
    height: 560px;
    max-width: 960px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
}

.hero-eyebrow {
    margin: 0 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #e02e30;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
}

.hero-title {
    margin: 0 0 12px;
    font-size: 34px;
    line-height: 1.22;
    font-weight: 800;
    color: #15181c;
    max-width: 640px;
    /* Балансируем многострочный заголовок: строки будут примерно равной длины,
       благодаря чему «рынке России» больше не висит сиротой на 2-й строке. */
    text-wrap: balance;
}

.hero-subtitle {
    margin: 0 0 26px;
    font-size: 14px;
    line-height: 1.45;
    color: #8a8f98;
    max-width: 520px;
}

.hero-form {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 720px;
    padding: 9px 9px 9px 16px;
    /* Тёмная рамка как на макете — задаёт визуальную глубину инпуту. */
    border: 1.5px solid #15181c;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.hero-form:focus-within {
    border-color: #15181c;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.hero-form-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #fff0f0;
}

.hero-input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-family: inherit;
    font-size: 15px;
    color: #23282c;
    padding: 11px 0;
}

.hero-input::placeholder {
    color: #b4b2a9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hero-send {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border: 0;
    border-radius: 12px;
    background: #e02e30;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.12s ease, opacity 0.15s ease;
}

.hero-send:not(:disabled):hover {
    background: #c41a1c;
}

.hero-send:not(:disabled):active {
    transform: scale(0.96);
}

.hero-send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.hero-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: 760px;
    margin-top: 20px;
}

.hero-chip {
    padding: 9px 16px;
    border: 1px solid #ececec;
    border-radius: 999px;
    background: #fff;
    font-family: inherit;
    font-size: 12.5px;
    line-height: 1.35;
    color: #3a3f47;
    text-align: left;
    cursor: pointer;
    max-width: 100%;
    white-space: normal;
    transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.hero-chip:hover {
    border-color: #e02e30;
    color: #e02e30;
}

.hero-trust {
    list-style: none;
    margin: 28px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 24px;
}

.hero-trust-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #8a8f98;
}

/* ── Запрос передан ────────────────────────────────────── */
.sent {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 40px 20px 36px;
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
}

.sent-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #e9f7ee;
    margin-bottom: 18px;
}

.sent-title {
    margin: 0 0 10px;
    font-size: 24px;
    line-height: 1.22;
    font-weight: 800;
    color: #15181c;
}

.sent-subtitle {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.45;
    color: #8a8f98;
    max-width: 460px;
}

.sent-query {
    margin: 0 0 24px;
    padding: 11px 18px;
    border-radius: 11px;
    background: #f6f6f4;
    color: #3a3f47;
    font-size: 14px;
    font-style: italic;
    max-width: 500px;
}

.sent-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.sent-open {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 22px;
    border-radius: 11px;
    background: #e30613;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    transition: background-color 0.15s ease;
}

.sent-open:hover {
    background: #c30410;
}

.sent-again {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 18px;
    border-radius: 11px;
    border: 1px solid #e4e4e4;
    background: #fff;
    color: #3a3f47;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;
}

.sent-again:hover {
    border-color: #c9c9c9;
    color: #15181c;
}

@media (max-width: 860px) {
    .hero-title {
        font-size: 26px;
    }
}

@media (max-width: 720px) {
    .hero {
        padding: 36px 16px 32px;
    }

    .hero-chips {
        gap: 6px;
    }

    .hero-title {
        font-size: 22px;
    }

    .hero-subtitle {
        font-size: 15px;
    }

    .hero-trust {
        gap: 14px;
        font-size: 12px;
    }

    .sent {
        padding: 40px 16px 32px;
    }

    .sent-title {
        font-size: 23px;
    }

    .sent-actions {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
    }

    .sent-open,
    .sent-again {
        width: 100%;
    }
}
</style>
